import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, Button, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getLangFromCookie, LANG_COOKIE } from "../../Utils/LangCookie";
import { getPrimaryColor } from "../GlobalFunction/Theme";

const TEXT = {
  ZH: {
    title: "404",
    subtitle: "找不到此頁面",
    desc: "您造訪的頁面不存在，可能已被移除或網址輸入錯誤。",
    backHome: "返回上一頁",
  },
  EN: {
    title: "404",
    subtitle: "Page not found",
    desc: "The page you are looking for does not exist or has been moved.",
    backHome: "Go back",
  },
  JP: {
    title: "404",
    subtitle: "ページが見つかりません",
    desc: "指定されたページは存在しないか、移動された可能性があります。",
    backHome: "戻る",
  },
};

const normalizeLang = (l) => (l === "EN" || l === "JP" ? l : "ZH");

export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ live language (no refresh required)
  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));

  // ✅ track if we can safely go back (avoid looping inside 404)
  const lastNon404Ref = useRef(null);

  useEffect(() => {
    // capture the last "non-404" page from router state, if you pass it
    // (safe even if absent)
    const from = location?.state?.from;
    if (from && typeof from === "string") {
      lastNon404Ref.current = from;
    }
  }, [location?.state]);

  useEffect(() => {
    const syncLang = () => setLang(normalizeLang(getLangFromCookie()));

    // 1) your app already dispatches this in many places
    window.addEventListener("app:languageChanged", syncLang);

    // 2) if your LangCookie helper also writes localStorage(LANG_COOKIE),
    // this will catch multi-tab or in-app updates without refresh.
    window.addEventListener("storage", (e) => {
      if (!e || !e.key) return;
      if (e.key === LANG_COOKIE) syncLang();
    });

    // 3) polling fallback (covers cookie-only changes that don't emit events)
    const id = window.setInterval(syncLang, 500);

    return () => {
      window.removeEventListener("app:languageChanged", syncLang);
      window.clearInterval(id);
    };
  }, []);

  const t = TEXT[lang] || TEXT.ZH;
  const txtColor = getPrimaryColor("b2b");

  const goBackSmart = () => {
    // If router gave us a safe "from" (best)
    const from = location?.state?.from;
    if (typeof from === "string" && from && from !== location.pathname) {
      navigate(from, { replace: true });
      return;
    }

    // If we captured a last non-404 route
    if (
      typeof lastNon404Ref.current === "string" &&
      lastNon404Ref.current &&
      lastNon404Ref.current !== location.pathname
    ) {
      navigate(lastNon404Ref.current, { replace: true });
      return;
    }

    // Otherwise try browser history back
    // (this is what user expects: "go back to where it came from")
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            sx={{ fontWeight: 900, letterSpacing: 2, color: txtColor }}
          >
            {t.title}
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: txtColor }}>
            {t.subtitle}
          </Typography>

          <Typography color="text.secondary">{t.desc}</Typography>

          <Button
            variant="contained"
            size="large"
            sx={{ mt: 2, px: 4 }}
            onClick={goBackSmart}
          >
            {t.backHome}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
